import { HttpClient, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Companies,
  Company,
  CompanyCustomer,
  CompanyMenu,
  CompanyReportsEmbed,
  CompanyStyles,
  ContactUsers,
  CustomerSource,
  DataTablesList,
  DomainTracking,
  EmailDomainDNSEmailModel,
  EmailDomainDNSEmailValidateModel,
  EmailDomainDNSModel,
  InviteValidate,
  OpenFormLink,
  SendInvites,
  User,
  UpdateTrackingRequest,
  UserRole,
  UsersPaged,
} from '@shared/definitions';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CompanyApiService {
  constructor(private http: HttpClient) {}

  addCompany = (newCompany: Company) => this.http.post('/api/v1.0/companies', newCompany);

  updateCompany = (companyForm: Company) => this.http.put<Company>(`/api/v1.0/companies/`, companyForm);

  getCompanies = () => this.http.get<Companies>('/api/v1.0/companies');

  getCompany = (companyId: string) => this.http.get<Companies>(`/api/v1.0/companies/${companyId}`);

  getReportsEmbedUrl = (companyId: string) =>
    this.http.get<CompanyReportsEmbed>(`/api/v1.0/companies/${companyId}/reports/embed-url`);

  getCompanyStyles = (companyId: string) => this.http.get<CompanyStyles>(`/api/v1.0/companies/styles/${companyId}`);

  getCompanyStylesByDomain = () => this.http.get<CompanyStyles>(`/api/v1.0/companies/styles`);

  getUsersPaged = (params) =>
    this.http.get<UsersPaged>(`/api/v1.0/companies/userspaged`, {
      params,
    });

  contactUsers = (req: ContactUsers) => this.http.post('/api/v1.0/companies/users/contact', req);

  sendInvites = (req: SendInvites) => this.http.post('/api/v1.0/companies/invites', req);

  validatesInvites = (req: InviteValidate) => this.http.post('/api/v1.0/companies/invites/validate', req);

  addLabelToUser = (companyId: string, params: any) =>
    this.http.post(`/api/v1.0/companies/${companyId}/userlabels`, params);

  removeLabelsFromUser = (companyId: string, params: any) =>
    this.http.delete(`/api/v1.0/companies/${companyId}/userlabels`, { body: params });

  addBulkLabelsToUser = (companyId: string, params: any) =>
    this.http.post(`/api/v1.0/companies/${companyId}/userlabels/bulk`, params);

  removeBulkLabelsFromUser = (companyId: string, params: any) =>
    this.http.delete(`/api/v1.0/companies/${companyId}/userlabels/bulk`, { body: params });

  postOpenFormLink = (params: OpenFormLink) => this.http.post(`/api/v1.0/companies/open-form-link`, params);

  putOpenFormLink = (req: OpenFormLink) => this.http.put('/api/v1.0/companies/open-form-link', req);

  getOpenFormLinkByCompId = (companyId: string) =>
    this.http.get<OpenFormLink>(`/api/v1.0/companies/open-form-link/company/${companyId}`);

  checkOpenFormLink = (openFormId: string) =>
    this.http.get<void>(`/api/v1.0/companies/open-form-link/${openFormId}/active`);

  joinOpenFormLink = (openFormId: string) =>
    this.http.post(`/api/v1.0/companies/open-form-link/${openFormId}/join`, {});

  getCompanyUsers = (companyId: string, params?) =>
    this.http.get<User[]>(`/api/v1.0/companies/${companyId}/users`, { params });

  getCompanyCustomers = (companyId: string, params?: GetCompanyCustomersQuery) =>
    this.http.get<DataTablesList<CompanyCustomer>>(`/api/v1.0/companies/${companyId}/customers`, {
      params: { ...params },
    });

  getCompanyCustomer = (companyId: string, customerId: string) =>
    this.http.get<CompanyCustomer>(`/api/v1.0/companies/${companyId}/customers/${customerId}`);

  deleteCompanyCustomer = (companyId: string, customerId: string) =>
    this.http.delete<void>(`/api/v1.0/companies/${companyId}/customers/${customerId}`);

  inviteCompanyUser = (companyId: string, email: string, role: UserRole) =>
    this.http.post(`/api/v1.0/companies/${companyId}/users`, { email, id: companyId, role });

  acceptLogin = (body: CompanyInviteAcceptModel, recaptchaToken: string): Observable<CompanyInviteAcceptedModel> =>
    this.http.post<CompanyInviteAcceptedModel>(`/api/v1.0/companies/invite/accept-login`, body, {
      headers: this.makeCaptchaHeaders(recaptchaToken),
    });

  acceptRegister = (body: CreateUserModel, recaptchaToken: string): Observable<CompanyInviteAcceptedModel> =>
    this.http.post<CompanyInviteAcceptedModel>(`/api/v1.0/companies/invite/accept-register`, body, {
      headers: this.makeCaptchaHeaders(recaptchaToken),
    });

  removeCompanyUser = (companyId: string, userID: string) =>
    this.http.delete(`/api/v1.0/companies/${companyId}/users/${userID}`);

  removeInvitation = (companyId: string, email: string) => {
    let params = new HttpParams();
    params = params.append('email', email);

    const req = new HttpRequest('DELETE', `/api/v1.0/companies/${companyId}/invite`, { params });
    return this.http.request(req);
  };

  acceptCompanyInvite = (inviteId: string) => this.http.post(`/api/v1.0/companies/invite/${inviteId}/accept`, {});

  getMenu = (companyId: string) => this.http.get<CompanyMenu>(`/api/v1.0/companies/${companyId}/menu`);

  // ── Company custom e-mail sending domains + sender addresses (dns-email-domain) ──
  getCompanyEmailDomains = (companyId: string) =>
    this.http.get<EmailDomainDNSModel[]>(`/api/v1.0/companies/dns-email-domain/${companyId}`);

  addCompanyEmailDomain = (model: EmailDomainDNSModel) =>
    this.http.post<EmailDomainDNSModel>(`/api/v1.0/companies/dns-email-domain`, model);

  updateCompanyEmailDomain = (model: EmailDomainDNSModel) =>
    this.http.put<EmailDomainDNSModel>(`/api/v1.0/companies/dns-email-domain`, model);

  deleteCompanyEmailDomain = (id: string) => this.http.delete<void>(`/api/v1.0/companies/dns-email-domain/${id}`);

  // Full response — the filename to save under comes from the `Content-Disposition` header, since the
  // export is an xlsx workbook unless `format=csv` is asked for.
  exportCompanyEmailDomains = (companyId: string, domainId?: string): Observable<HttpResponse<Blob>> =>
    this.http.get(`/api/v1.0/companies/dns-email-domain/${companyId}/export`, {
      responseType: 'blob',
      observe: 'response',
      params: domainId ? { domain_id: domainId } : {},
    });

  validateCompanyEmailDomain = (id: string) =>
    this.http.post<EmailDomainDNSModel>(`/api/v1.0/companies/dns-email-domain/validate/${id}`, {});

  getCompanyEmailSenders = (companyId: string) =>
    this.http.get<{ data: EmailDomainDNSEmailModel[] }>(`/api/v1.0/companies/dns-email-domain/emails`, {
      params: { company: companyId },
    });

  addCompanyEmailSender = (model: EmailDomainDNSEmailModel) =>
    this.http.post<EmailDomainDNSEmailModel>(`/api/v1.0/companies/dns-email-domain/emails`, model);

  updateCompanyEmailSender = (model: EmailDomainDNSEmailModel) =>
    this.http.put<EmailDomainDNSEmailModel>(`/api/v1.0/companies/dns-email-domain/emails`, model);

  deleteCompanyEmailSender = (id: string) =>
    this.http.delete<void>(`/api/v1.0/companies/dns-email-domain/emails/${id}`);

  resendCompanyEmailSenderCode = (id: string) =>
    this.http.post<void>(`/api/v1.0/companies/dns-email-domain/emails/${id}/activate`, {});

  activateCompanyEmailSender = (model: EmailDomainDNSEmailValidateModel) =>
    this.http.post<void>(`/api/v1.0/companies/dns-email-domain/emails/activate`, model);

  updateCompanyEmailDomainTracking = (id: string, model: UpdateTrackingRequest) =>
    this.http.put<DomainTracking>(`/api/v1.0/companies/dns-email-domain/tracking/${id}`, model);

  // Also the "finish the job" call: on an already-active certificate it re-issues nothing and just
  // lets the backend raise web_scheme to https.
  regenerateCompanyEmailDomainTrackingCertificate = (id: string) =>
    this.http.post<DomainTracking>(`/api/v1.0/companies/dns-email-domain/tracking-certificate/${id}`, {});

  private makeCaptchaHeaders(recaptchaToken: string) {
    return {
      'X-Captcha-Response': recaptchaToken,
      'X-Captcha-Site-Key': ' this.config.recaptchaSiteKey', //TODO: handle
    };
  }
}

export interface GetCompanyCustomersQuery {
  source?: CustomerSource;
  newsletter_subscribed?: boolean;
  has_user?: boolean;
  'search.value'?: string;
  order_column_name?: string;
  order_column_descending?: boolean;
  draw?: number;
  start?: number;
  length?: number;
}

export interface CompanyInviteAcceptModel {
  invitation_id: string;
  email: string;
  password: string;
  shop_id: string | null;
}

export interface CreateUserModel {
  email: string;
  password: string;
  password_confirm: string;
  invitation_id: string;
  first_name: string;
  last_name: string;
  // country: "string",
  // city: "string",
  // post_code: "string",
  // street: "string",
  // house_number: "string",
  // phone: "string",
  // nickname: "string",
  // description: "string",
  // gender: "string",
}

export interface CompanyInviteAcceptedModel {
  redirect_url: string;
  redirect: boolean;
  existed: boolean;
  company_id: boolean;
}
